import { useCallback, useEffect, useRef, useState } from 'react';
import { teamService } from '../services/teams/teamService.js';

const EMPTY_LOADS = {};

/**
 * Cargas de tareas por equipo para una fecha.
 *
 * `scheduledDate` debe ser la clave `YYYY-MM-DD`, nunca un objeto Date: un Date cambia
 * de referencia en cada render y dispararia dos peticiones por navegacion de dia.
 */
export const useTeamLoads = (scheduledDate, { enabled = true, refreshToken = 0 } = {}) => {
  const [loadsByTeamId, setLoadsByTeamId] = useState(EMPTY_LOADS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);
  const previousDateRef = useRef(null);

  const fetchLoads = useCallback(async ({ keepPreviousData = false } = {}) => {
    if (!enabled || !scheduledDate) {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!keepPreviousData) {
      setLoading(true);
    }
    setError(null);

    try {
      const loads = await teamService.getTeamLoads(scheduledDate);

      // Descarta las respuestas de peticiones ya superadas por otra mas nueva
      if (requestId !== requestIdRef.current) return;

      setLoadsByTeamId(loads);
    } catch (fetchError) {
      if (requestId !== requestIdRef.current) return;

      console.error('Error cargando las cargas por equipo:', fetchError);
      setError(fetchError);
      setLoadsByTeamId(EMPTY_LOADS);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, scheduledDate]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Cambio de fecha: se vuelve al placeholder.
    // Misma fecha (refresco tras operar sobre una tarea): se mantienen las cards visibles.
    const isSameDate = previousDateRef.current === scheduledDate;
    previousDateRef.current = scheduledDate;

    fetchLoads({ keepPreviousData: isSameDate });
  }, [enabled, fetchLoads, refreshToken, scheduledDate]);

  const refetch = useCallback(() => fetchLoads({ keepPreviousData: true }), [fetchLoads]);

  return { loadsByTeamId, loading, error, refetch };
};
